import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Clock, 
  Award, 
  Trophy,
  Target,
  Play,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  HelpCircle,
  Star,
  RefreshCw
} from 'lucide-react';
import { CelebrationOverlay } from '../components/CelebrationOverlay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuizDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Quiz data states
  const [quiz, setQuiz] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Quiz taking states
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  
  // Dialog states
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  
  // Celebration
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState(null);

  // Fetch quiz data
  const fetchQuizData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch quiz details using the dedicated endpoint
      const quizRes = await axios.get(`${API_URL}/api/standalone-quizzes/${id}`, { headers });
      const quizData = quizRes.data.quiz;
      
      if (!quizData) {
        setError('Quiz not found');
        return;
      }
      
      setQuiz(quizData);
      setAssignment(quizRes.data.assignment);
      setAttempts(quizRes.data.attempts || []);
      
    } catch (err) {
      console.error('Error fetching quiz:', err);
      setError('Failed to load quiz');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchQuizData();
  }, [fetchQuizData]);

  // Calculate quiz status
  const getQuizStatus = () => {
    if (!quiz) return { status: 'loading', label: 'Loading...' };
    
    const maxAttempts = quiz.max_attempts || 3;
    const usedAttempts = attempts.length;
    const remainingAttempts = maxAttempts - usedAttempts;
    const passingScore = quiz.passing_score || 70;
    
    // Check if any attempt passed
    const passedAttempt = attempts.find(a => a.passed);
    
    if (passedAttempt) {
      return {
        status: 'passed',
        label: 'Passed',
        score: passedAttempt.score,
        message: `Congratulations! You passed with ${passedAttempt.score}%`,
        variant: 'success'
      };
    }
    
    if (remainingAttempts <= 0) {
      const bestScore = Math.max(...attempts.map(a => a.score), 0);
      return {
        status: 'failed',
        label: 'Failed',
        score: bestScore,
        message: 'No attempts left. Please contact your manager.',
        variant: 'destructive',
        remainingAttempts: 0
      };
    }
    
    if (usedAttempts === 0) {
      return {
        status: 'pending',
        label: 'Not Attempted',
        message: `${remainingAttempts} attempt${remainingAttempts > 1 ? 's' : ''} remaining`,
        variant: 'secondary',
        remainingAttempts
      };
    }
    
    const bestScore = Math.max(...attempts.map(a => a.score), 0);
    return {
      status: 'in_progress',
      label: 'In Progress',
      score: bestScore,
      message: `Best score: ${bestScore}%. ${remainingAttempts} attempt${remainingAttempts > 1 ? 's' : ''} remaining. Required: ${passingScore}%`,
      variant: 'warning',
      remainingAttempts
    };
  };

  const quizStatus = getQuizStatus();

  // Start quiz
  const handleStartQuiz = () => {
    if (quizStatus.status === 'failed') {
      toast.error('No attempts remaining. Please contact your manager.');
      return;
    }
    if (quizStatus.status === 'passed') {
      toast.info('You have already passed this quiz!');
      return;
    }
    setQuizStarted(true);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setQuizResult(null);
  };

  // Handle answer selection
  const handleAnswerSelect = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  // Handle cancel quiz with confirmation
  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    setQuizStarted(false);
    setCurrentQuestionIndex(0);
    setAnswers({});
    toast.info('Quiz cancelled. You can try again later.');
  };

  // Navigate questions
  const handleNextQuestion = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  // Submit quiz
  const handleSubmitQuiz = async () => {
    const unanswered = quiz.questions.filter(q => !answers[q.id]).length;
    if (unanswered > 0) {
      toast.warning(`Please answer all questions. ${unanswered} question${unanswered > 1 ? 's' : ''} remaining.`);
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const response = await axios.post(
        `${API_URL}/api/standalone-quizzes/${id}/attempt`,
        answers,
        { headers }
      );
      
      setQuizResult(response.data);
      setQuizStarted(false);
      
      if (response.data.passed) {
        // Show celebration
        setCelebrationData({
          title: 'Congratulations!',
          message: 'You passed the quiz!',
          score: response.data.score,
          courseName: quiz.title,
          type: 'post_test'
        });
        setShowCelebration(true);
      } else {
        setShowResultDialog(true);
      }
      
      // Refresh data
      await fetchQuizData();
      
    } catch (err) {
      console.error('Error submitting quiz:', err);
      toast.error('Failed to submit quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 max-w-4xl">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-48 w-full mb-4" />
          <Skeleton className="h-32 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error || !quiz) {
    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 max-w-4xl">
          <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-red-700 dark:text-red-400 mb-2">Quiz Not Found</h2>
              <p className="text-muted-foreground mb-4">{error || 'This quiz does not exist or you do not have access.'}</p>
              <Button onClick={() => navigate('/courses')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Courses
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Quiz in progress view
  if (quizStarted && quiz.questions?.length > 0) {
    const currentQuestion = quiz.questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;
    const filteredOptions = currentQuestion.options?.filter(o => o && o.trim() !== '') || [];

    return (
      <DashboardLayout>
        <div className="container mx-auto p-6 max-w-3xl">
          {/* Quiz Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                <HelpCircle className="w-3 h-3 mr-1" />
                Quiz
              </Badge>
              <h1 className="text-xl font-bold">{quiz.title}</h1>
            </div>
            <Button 
              variant="outline" 
              onClick={handleCancelClick}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="cancel-quiz-btn"
            >
              Cancel Quiz
            </Button>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-muted-foreground mb-2">
              <span>Question {currentQuestionIndex + 1} of {quiz.questions.length}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Question Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">
                Q{currentQuestionIndex + 1}. {currentQuestion.question}
              </CardTitle>
              {currentQuestion.type === 'multiple_choice' && (
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-2 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Multiple choice question - Select all correct options
                </p>
              )}
            </CardHeader>
            <CardContent>
              {currentQuestion.type === 'multiple_choice' ? (
                // Multiple Choice - Checkboxes
                <div className="space-y-3">
                  {filteredOptions.map((option, idx) => {
                    const selectedAnswers = Array.isArray(answers[currentQuestion.id]) 
                      ? answers[currentQuestion.id] 
                      : [];
                    const isSelected = selectedAnswers.includes(option);
                    
                    const handleCheckboxChange = () => {
                      const current = Array.isArray(answers[currentQuestion.id]) 
                        ? [...answers[currentQuestion.id]] 
                        : [];
                      if (isSelected) {
                        handleAnswerSelect(currentQuestion.id, current.filter(o => o !== option));
                      } else {
                        handleAnswerSelect(currentQuestion.id, [...current, option]);
                      }
                    };
                    
                    return (
                      <div
                        key={idx}
                        className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50 hover:bg-muted/50'
                        }`}
                        onClick={handleCheckboxChange}
                      >
                        <Checkbox 
                          checked={isSelected}
                          id={`mc-option-${idx}`}
                        />
                        <Label htmlFor={`mc-option-${idx}`} className="flex-1 cursor-pointer font-normal">
                          {option}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Single Choice - Radio Buttons
                <RadioGroup
                  value={answers[currentQuestion.id] || ''}
                  onValueChange={(value) => handleAnswerSelect(currentQuestion.id, value)}
                  className="space-y-3"
                >
                  {filteredOptions.map((option, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer ${
                        answers[currentQuestion.id] === option
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                      onClick={() => handleAnswerSelect(currentQuestion.id, option)}
                    >
                      <RadioGroupItem value={option} id={`option-${idx}`} />
                      <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer font-normal">
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0}
              data-testid="prev-question-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            
            {currentQuestionIndex === quiz.questions.length - 1 ? (
              <Button
                onClick={handleSubmitQuiz}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="submit-quiz-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Quiz'}
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleNextQuestion}
                data-testid="next-question-btn"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>

          {/* Question Navigator */}
          <Card className="mt-6">
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Question Navigator</CardTitle>
            </CardHeader>
            <CardContent className="py-3">
              <div className="flex flex-wrap gap-2">
                {quiz.questions.map((q, idx) => (
                  <Button
                    key={q.id}
                    variant={answers[q.id] ? 'default' : 'outline'}
                    size="sm"
                    className={`w-10 h-10 ${
                      idx === currentQuestionIndex
                        ? 'ring-2 ring-primary ring-offset-2'
                        : ''
                    } ${answers[q.id] ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setCurrentQuestionIndex(idx)}
                  >
                    {idx + 1}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                <span className="inline-flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-green-600"></span> Answered
                </span>
                <span className="inline-flex items-center gap-1 ml-4">
                  <span className="w-3 h-3 rounded border border-border"></span> Unanswered
                </span>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Cancel Confirmation Dialog */}
        <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Quiz?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel this quiz? Your progress will not be saved and this will count as an attempt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-dialog-no">Continue Quiz</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmCancel}
                className="bg-red-600 hover:bg-red-700"
                data-testid="cancel-dialog-yes"
              >
                Yes, Cancel Quiz
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DashboardLayout>
    );
  }

  // Quiz Home Page View
  return (
    <DashboardLayout>
      <div className="container mx-auto p-6 max-w-4xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate('/courses')}
          data-testid="back-to-courses"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Courses
        </Button>

        {/* Quiz Header Card */}
        <Card className="mb-6 border-purple-200 dark:border-purple-800 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <Badge className="bg-white/20 text-white mb-3">
                  <HelpCircle className="w-3 h-3 mr-1" />
                  Standalone Quiz
                </Badge>
                <h1 className="text-2xl font-bold mb-2">{quiz.title}</h1>
                <p className="text-purple-100">{quiz.description || 'Test your knowledge with this quiz'}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 text-purple-100">
                  <Trophy className="w-5 h-5" />
                  <span className="font-semibold">+{quiz.points_reward || 50} points</span>
                </div>
              </div>
            </div>
          </div>

          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <HelpCircle className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{quiz.questions?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Questions</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Clock className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{quiz.duration_minutes || 15}</p>
                <p className="text-xs text-muted-foreground">Minutes</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Target className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{quiz.passing_score || 70}%</p>
                <p className="text-xs text-muted-foreground">Passing Score</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <RefreshCw className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{quiz.max_attempts || 3}</p>
                <p className="text-xs text-muted-foreground">Max Attempts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Card */}
        <Card className={`mb-6 ${
          quizStatus.status === 'passed' ? 'border-green-300 bg-green-50 dark:bg-green-900/20' :
          quizStatus.status === 'failed' ? 'border-red-300 bg-red-50 dark:bg-red-900/20' :
          quizStatus.status === 'in_progress' ? 'border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20' :
          'border-gray-300'
        }`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {quizStatus.status === 'passed' && (
                  <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                )}
                {quizStatus.status === 'failed' && (
                  <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center">
                    <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                  </div>
                )}
                {quizStatus.status === 'in_progress' && (
                  <div className="w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-800 flex items-center justify-center">
                    <AlertTriangle className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                  </div>
                )}
                {quizStatus.status === 'pending' && (
                  <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <Clock className="w-8 h-8 text-gray-500" />
                  </div>
                )}
                
                <div>
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    Quiz Status: 
                    <Badge variant={quizStatus.variant} className={`text-sm ${
                      quizStatus.status === 'passed' ? 'bg-green-600' :
                      quizStatus.status === 'failed' ? 'bg-red-600' :
                      quizStatus.status === 'in_progress' ? 'bg-yellow-600' :
                      ''
                    }`}>
                      {quizStatus.label}
                      {quizStatus.score !== undefined && ` (${quizStatus.score}%)`}
                    </Badge>
                  </h3>
                  <p className="text-muted-foreground mt-1">{quizStatus.message}</p>
                </div>
              </div>

              {/* Action Button */}
              {quizStatus.status !== 'passed' && quizStatus.status !== 'failed' && (
                <Button
                  size="lg"
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={handleStartQuiz}
                  data-testid="start-quiz-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  {quizStatus.status === 'pending' ? 'Start Quiz' : 'Retry Quiz'}
                </Button>
              )}
            </div>

            {/* Attempt History */}
            {attempts.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-semibold mb-3">Attempt History</h4>
                <div className="space-y-2">
                  {attempts.map((attempt, idx) => (
                    <div
                      key={attempt.id}
                      className="flex items-center justify-between p-3 bg-background rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">Attempt {attempts.length - idx}</span>
                        <Badge variant={attempt.passed ? 'default' : 'secondary'} className={attempt.passed ? 'bg-green-600' : ''}>
                          {attempt.score}%
                        </Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date(attempt.completed_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assignment Info */}
        {assignment && (
          <Card>
            <CardContent className="p-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                Assignment Details
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Assigned On:</span>
                  <p className="font-medium">{new Date(assignment.assigned_at).toLocaleDateString()}</p>
                </div>
                {assignment.due_date && (
                  <div>
                    <span className="text-muted-foreground">Due Date:</span>
                    <p className="font-medium">{new Date(assignment.due_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Result Dialog (for failed attempts) */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-6 h-6 text-red-500" />
              Quiz Not Passed
            </DialogTitle>
            <DialogDescription>
              You scored {quizResult?.score}%. The passing score is {quizResult?.passing_score}%.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="text-center">
              <p className="text-4xl font-bold text-red-500 mb-2">{quizResult?.score}%</p>
              <p className="text-muted-foreground">
                {quizResult?.correct_count} out of {quizResult?.total_questions} correct
              </p>
              {quizStatus.remainingAttempts > 0 && (
                <p className="mt-4 text-sm">
                  You have <strong>{quizStatus.remainingAttempts}</strong> attempt{quizStatus.remainingAttempts > 1 ? 's' : ''} remaining.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResultDialog(false)}>
              Close
            </Button>
            {quizStatus.remainingAttempts > 0 && (
              <Button onClick={() => { setShowResultDialog(false); handleStartQuiz(); }}>
                Try Again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Celebration Overlay */}
      <CelebrationOverlay
        isVisible={showCelebration}
        onClose={() => {
          setShowCelebration(false);
          setCelebrationData(null);
        }}
        title={celebrationData?.title}
        message={celebrationData?.message}
        score={celebrationData?.score}
        courseName={celebrationData?.courseName}
        type={celebrationData?.type}
      />
    </DashboardLayout>
  );
};

export default QuizDetail;
